import React from 'react';
import {
  Paper, Stack, Typography, Autocomplete, TextField, Chip, Box, Alert,
} from '@mui/material';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import { useFamily } from '../context/FamilyContext';
import { useAuth } from '../context/AuthContext';

const ROLE_COLOR = { bride_family: 'primary', groom_family: 'info' };

const formatCategory = (c) => c.replace(/_/g, ' ').replace(/\b\w/g, ch => ch.toUpperCase());

// Admin bar shown on family service pages: choose which bride/groom family's details to manage.
export default function FamilySelector({ families, loading, required, compact = false }) {
  const { family, setFamily } = useFamily();
  const { user } = useAuth();
  const isMatchmaker = user?.role?.name === 'matchmaker';
  const current = family ? (families.find(f => f.id === family.id) || family) : null;
  const noun = isMatchmaker ? 'client' : 'family';
  const emptyText = isMatchmaker
    ? 'No clients yet — create a Service Wizard wishlist for a family to manage them here.'
    : 'No bride/groom families yet.';

  const picker = (
    <Autocomplete
      sx={{ flex: 1, minWidth: compact ? 0 : 260 }}
      size="small"
      loading={loading}
      options={families}
      value={current}
      noOptionsText={emptyText}
      onChange={(_, value) => setFamily(value || null)}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => (compact ? o.displayName : `${o.displayName} (${o.email})`)}
      groupBy={(o) => o.roleDisplayName || o.roleName}
      renderOption={(props, o) => (
        <li {...props} key={o.id}>
          <Box>
            <Typography variant="body2" fontWeight={600}>{o.displayName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {o.email} · {o.serviceCategories.length
                ? `${o.serviceCategories.length} service(s) selected`
                : 'no services selected yet'}
            </Typography>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField {...params} label={compact ? `Managing ${noun}` : undefined} placeholder={`Select a ${isMatchmaker ? 'client' : 'bride / groom'} family…`} />
      )}
    />
  );

  if (compact) return picker;

  return (
    <Box sx={{ mb: 3 }}>
      <Paper variant="outlined" sx={{ p: 2, borderColor: current ? 'primary.main' : 'warning.main' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 190 }}>
            <FamilyRestroomIcon color={current ? 'primary' : 'warning'} />
            <Typography fontWeight={700}>Managing {noun}</Typography>
          </Stack>
          {picker}
          {current && (
            <Chip
              label={current.roleDisplayName || current.roleName}
              color={ROLE_COLOR[current.roleName] || 'default'}
              size="small"
            />
          )}
        </Stack>
        {current && (
          <Stack direction="row" spacing={0.5} mt={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
              Services selected by this family:
            </Typography>
            {current.serviceCategories?.length
              ? current.serviceCategories.map(c => (
                <Chip key={c} label={formatCategory(c)} size="small" variant="outlined" color="success" />
              ))
              : <Typography variant="caption" color="text.secondary">none yet</Typography>}
          </Stack>
        )}
      </Paper>
      {required && !current && (
        <Alert severity="info" sx={{ mt: 2 }}>
          Select a {isMatchmaker ? 'client ' : 'bride or groom '}family above to view, add, update or delete their details for this service.
        </Alert>
      )}
    </Box>
  );
}
